import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
} from "firebase/firestore";
import { db } from "../services/firebase";

const PAGE_SIZE = 20;

export default function AdminRoles() {
  const [rows, setRows] = useState([]); // [{uid, email, displayName, photoURL, roles:{}}]
  const [loading, setLoading] = useState(false);
  const [qState, setQState] = useState({ search: "" });
  const [page, setPage] = useState({ cursor: null, hasMore: true });
  const [busyUid, setBusyUid] = useState(null);

  const filtered = useMemo(() => {
    const s = qState.search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((u) =>
      [u.displayName, u.email, u.uid].some((x) =>
        (x || "").toLowerCase().includes(s)
      )
    );
  }, [rows, qState.search]);

  async function fetchPage(reset = false) {
    setLoading(true);
    try {
      let qy = query(collection(db, "users"), orderBy("lastLoginAt", "desc"), limit(PAGE_SIZE));
      if (!reset && page.cursor) {
        qy = query(collection(db, "users"), orderBy("lastLoginAt", "desc"), startAfter(page.cursor), limit(PAGE_SIZE));
      }
      const snap = await getDocs(qy);
      const base = reset ? [] : rows.slice();
      const next = snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
      // fetch roles for each
      await Promise.all(
        next.map(async (u) => {
          try {
            const rs = await getDoc(doc(db, "roles", u.uid));
            u.roles = rs.exists() ? rs.data() : {};
          } catch {
            u.roles = {};
          }
        })
      );
      setRows([...base, ...next]);
      setPage({
        cursor: snap.docs.length ? snap.docs[snap.docs.length - 1] : null,
        hasMore: snap.docs.length === PAGE_SIZE,
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function toggleRole(u, key) {
    const current = !!(u.roles && u.roles[key]);
    const newVal = !current;

    // Optimistic UI
    const prev = { ...u.roles };
    u.roles = { ...u.roles, [key]: newVal };
    setRows((arr) => arr.map((x) => (x.uid === u.uid ? { ...u } : x)));
    setBusyUid(u.uid);

    try {
      await setDoc(
        doc(db, "roles", u.uid),
        { [key]: newVal, updatedAt: new Date() },
        { merge: true }
      );
    } catch (e) {
      // revert on error
      u.roles = prev;
      setRows((arr) => arr.map((x) => (x.uid === u.uid ? { ...u } : x)));
      alert("Erreur lors de la mise à jour des rôles.");
    } finally {
      setBusyUid(null);
    }
  }

  return (
    <div className="container py-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h3 className="mb-0">👥 Gestion des rôles</h3>
          <small className="text-muted">Attribuez <code>cashier</code>, <code>manager</code>, <code>admin</code> aux utilisateurs.</small>
        </div>
        <div style={{ minWidth: 280 }}>
          <input
            className="form-control"
            placeholder="Rechercher (nom, email, uid)"
            value={qState.search}
            onChange={(e) => setQState((s) => ({ ...s, search: e.target.value }))}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th style={{ width: 56 }}></th>
                  <th>Utilisateur</th>
                  <th>Email</th>
                  <th>UID</th>
                  <th className="text-center">Cashier</th>
                  <th className="text-center">Manager</th>
                  <th className="text-center">Admin</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-muted">
                      {loading ? "Chargement…" : "Aucun utilisateur"}
                    </td>
                  </tr>
                ) : (
                  filtered.map((u) => (
                    <tr key={u.uid}>
                      <td>
                        {u.photoURL ? (
                          <img
                            src={u.photoURL}
                            alt=""
                            style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover" }}
                          />
                        ) : (
                          <div
                            className="bg-light d-flex align-items-center justify-content-center"
                            style={{ width: 40, height: 40, borderRadius: 8 }}
                          >
                            <span className="text-muted">{(u.displayName || u.email || "?")?.[0]?.toUpperCase()}</span>
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="fw-semibold">{u.displayName || "—"}</div>
                        <div className="small text-muted">{u.providerId || ""}</div>
                      </td>
                      <td>{u.email || "—"}</td>
                      <td className="small text-muted">{u.uid}</td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={!!(u.roles && u.roles.cashier)}
                          onChange={() => toggleRole(u, "cashier")}
                          disabled={busyUid === u.uid}
                        />
                      </td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={!!(u.roles && u.roles.manager)}
                          onChange={() => toggleRole(u, "manager")}
                          disabled={busyUid === u.uid}
                        />
                      </td>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={!!(u.roles && u.roles.admin)}
                          onChange={() => toggleRole(u, "admin")}
                          disabled={busyUid === u.uid}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center">
            <div className="small text-muted">
              {rows.length} utilisateur(s){loading ? " • chargement…" : ""}
            </div>
            {page.hasMore && (
              <button className="btn btn-outline-dark" onClick={() => fetchPage(false)} disabled={loading}>
                Charger plus
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="alert alert-warning mt-3 py-2">
        <strong>Astuce :</strong> pour voir des utilisateurs ici, ils doivent s’être connectés au moins une fois (Google ou Email).
      </div>
    </div>
  );
}
